import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { fileNameFor } from "./exportPdf";
import { fetchPhotos, photoReport, assetBytes } from "./photos";
import logoArt from "../../../momentum_logo.png";

/**
 * The findings register as a spreadsheet.
 *
 * A third consumer of the same model, not a third implementation — which is
 * the whole reason the model exists (design §7a). The existing ExportExcel.js
 * is a separate 1,636-line rebuild of the report with 29 of its own
 * `reportType` checks; nothing here touches it.
 *
 * Excel always produces the register, in both layouts. Elaborate is a
 * print-layout idea — one entry per finding, set out down the page — and it
 * does not translate to a spreadsheet. What a client wants from Excel is to
 * filter and sort the findings themselves, which is exactly the register.
 */

const C = {
  ink: "FF1E2A28",
  tealDark: "FF245952",
  white: "FFFFFFFF",
  sunk: "FFF1F4F3",
  gold: "FFEFC71D",
  yes: "FF2E7D4F",
  partial: "FFB8860B",
  no: "FFC0392B",
  na: "FF7C8A87",
};

const statusFill = (s) =>
  s === "Yes" ? C.yes : s === "Partial" ? C.partial : s === "No" ? C.no : C.na;

/**
 * Excel column widths are in characters; the model's are percentages of a
 * printed page. Multiplying gets close enough, with a floor so the narrow
 * columns stay readable and a ceiling so Observation does not run off screen.
 */
const widthFor = (col) => Math.min(60, Math.max(8, Math.round(col.width * 1.9)));

function styleHeader(row) {
  row.font = { bold: true, size: 9, color: { argb: C.white } };
  row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.tealDark } };
  row.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  row.height = 26;
}

/* ------------------------------------------------------------------ */

function summarySheet(wb, model, mark) {
  const ws = wb.addWorksheet("Summary", {
    properties: { defaultColWidth: 22 },
    views: [{ showGridLines: false }],
  });
  stampLogo(ws, mark);

  const h = model.header;
  const s = model.summary;

  ws.addRow(["Critical Safety Assessment"]).font = {
    bold: true, size: 16, color: { argb: C.tealDark },
  };
  ws.addRow([]);

  const pairs = [
    ["Client", h.organization],
    ["Site", h.site],
    ["Assessment dates", [h.start_date, h.end_date].filter(Boolean).join(" to ") || "—"],
    ["Framework version", h.framework_version],
    ["Report reference", h.report_id],
    // A spreadsheet has no page footer, so the issue goes here — a client
    // filtering the register still needs to know which document they have.
    ...(h.issue
      ? [
          ["Issue", `${h.issue.no}  ·  ${new Date(h.issue.issued_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`],
          ["This issue reports", `${h.issue.selected_count} of ${h.issue.assessed_count} assessed findings`],
        ]
      : []),
    ["Prepared by", h.prepared_by || "—"],
    // Stated rather than left blank: review is optional and never blocks
    // issuing, which is only honest if the document says which happened (§7c).
    ["Reviewed by", h.was_reviewed ? h.reviewed_by || "Reviewed" : "Not reviewed"],
    [],
    ["Overall conformance", s.overall_pct === null ? "Not assessed" : s.overall_pct / 100],
    ["Maturity band", s.maturity_band || "—"],
    [],
    ["Questions assessed", s.counts.assessed],
    ["Conformant", s.counts.yes],
    ["Partially conformant", s.counts.partial],
    ["Non-conformant", s.counts.no],
    ["Not applicable", s.counts.na],
    ["Findings in this report", s.selected],
  ];

  pairs.forEach((p) => {
    const row = ws.addRow(p);
    if (p.length) {
      row.getCell(1).font = { bold: true, size: 10 };
      row.getCell(2).alignment = { horizontal: "left" };

      /*
       * Formatted by label, not by row number. Two rows above are conditional
       * on whether this is an issue or a preview, so a hardcoded index would
       * silently format the wrong cell — or none — depending on how the export
       * was started.
       */
      if (p[0] === "Overall conformance" && typeof p[1] === "number") {
        row.getCell(2).numFmt = "0.0%";
      }
    }
  });

  ws.getColumn(1).width = 26;
  ws.getColumn(2).width = 46;

  return ws;
}

/*
 * Room for a thumbnail.
 *
 * Excel measures row height in points and column width in characters, neither
 * of which is the image's pixels — these leave the picture room rather than
 * matching it exactly. An image is anchored over a range rather than placed
 * inside a cell, so a row that is too short has the picture spilling across
 * its neighbours.
 */
const PHOTO_PX = { width: 96, height: 72 };
const PHOTO_ROW_HEIGHT = 58;
const PHOTO_COL_WIDTH = 16;

/*
 * The picture gets a column of its own, next to the one holding the URL.
 *
 * An image cannot live *in* a cell — Excel anchors it over a range and draws
 * it on top. Putting it over the URL column meant a picture covering the text
 * underneath: the value was still there for sorting and copying, but a reader
 * could not see both, and selecting the cell to copy the link meant clicking
 * behind a photograph. Two columns keeps each thing legible and leaves the
 * register sortable.
 */
const PHOTO_COL_LABEL = "Photo";

/* The wordmark, on its own band above each sheet's header row. */
/*
 * 200 wide is what the HSE and Electrical workbooks have used for years, so a
 * client who files all three sees the same mark at the same size. The height
 * follows the artwork own 3.89:1 rather than HSE hardcoded 60, which stretched
 * a differently proportioned file.
 */
const LOGO_PX = { width: 200, height: 51 };
const LOGO_ROW_HEIGHT = 42;

/**
 * Put the wordmark at the top of a sheet.
 *
 * Excel has no repeating page header that can carry an image — ExcelJS writes
 * the ampersand-G marker but not the legacyDrawingHF part it refers to, so the
 * reference dangles and nothing appears. A band above the header row is what
 * can actually be done, and it is visible on screen and on the first printed
 * page.
 *
 * Called before any other row, so the header row lands at row 2 and the frozen
 * pane and filter are set against that.
 *
 * Takes an image id rather than bytes, so the four sheets share one copy of the
 * file instead of embedding it four times.
 */
function stampLogo(ws, id) {
  // Not a falsiness test: ExcelJS numbers its images from zero, so the first
  // one registered has id 0 and `if (!id)` would drop it from every sheet.
  if (id === null || id === undefined) return 0;
  try {
    ws.addRow([]);
    ws.getRow(1).height = LOGO_ROW_HEIGHT;
    ws.addImage(id, { tl: { col: 0.15, row: 0.15 }, ext: LOGO_PX, editAs: "oneCell" });
    return 1;
  } catch (e) {
    return 0;                 // a sheet without a logo beats no sheet
  }
}

function findingsSheet(wb, model, mark) {
  const data = model.sections.find((x) => x.key === "findings").data;
  const cols = data.columns;

  const ws = wb.addWorksheet("Findings");
  const top = stampLogo(ws, mark);
  // Frozen below the header, wherever the logo band left it.
  ws.views = [{ state: "frozen", ySplit: top + 1 }];

  ws.columns = [
    ...cols.map((c) => ({ key: c.key, width: widthFor(c) })),
    // Added here rather than in the shared column set: a picture column is an
    // Excel presentation choice, and the PDF and Word renderers read the same
    // list and would each have to learn to ignore it.
    { key: "photo_image", width: PHOTO_COL_WIDTH },
  ];
  styleHeader(ws.addRow([...cols.map((c) => c.label), PHOTO_COL_LABEL]));

  data.rows.forEach((r) => {
    const row = ws.addRow([
      r.sr_no,
      r.pillar,
      r.sub_pillar,
      r.verification_method || "",
      r.status,
      r.observation,
      r.recommendation || "",
      r.legal_reference || "",
      r.score === null ? "" : r.score,
      // The URLs, not the images. A spreadsheet a client filters is not a
      // photo album, and embedding would multiply the file size for nothing.
      (r.photos || []).join("\n"),
    ]);

    row.alignment = { vertical: "top", wrapText: true };
    row.getCell(5).font = { bold: true, color: { argb: C.white } };
    row.getCell(5).fill = {
      type: "pattern", pattern: "solid", fgColor: { argb: statusFill(r.status) },
    };
    row.getCell(5).alignment = { vertical: "top", horizontal: "center" };
    row.getCell(1).alignment = { vertical: "top", horizontal: "center" };
    row.getCell(9).alignment = { vertical: "top", horizontal: "center" };

  });

  /*
   * The filter covers the data columns only. A picture column has nothing to
   * filter on, and including it puts a dropdown arrow on an empty header.
   */
  if (data.rows.length) {
    ws.autoFilter = {
      from: { row: top + 1, column: 1 },
      to: { row: top + 1, column: cols.length },
    };
  }

  return ws;
}

/**
 * Put the photographs in, once the workbook is otherwise finished.
 *
 * A separate pass on purpose. The workbook is complete and valid before this
 * runs, so a picture that will not embed costs the reader a picture rather
 * than the file — and the two halves can be read, and tested, on their own.
 *
 * It finds its own rows, by reading the URLs out of the Evidence column,
 * rather than being handed a map of where they landed. That keeps
 * `buildWorkbook` returning a plain workbook, and means this can be run
 * against any register the builder produced without the two having to agree
 * on a private handle.
 *
 * Insertion is sequential because it is cheap and in memory. It is the
 * *fetching* that must not be, and that happens before this is called.
 *
 * Never throws: a picture that will not embed leaves that row with its URL.
 */
export function insertPhotos(wb, images) {
  if (!wb || !images || !images.size) return 0;

  const ws = wb.getWorksheet("Findings");
  if (!ws) return 0;

  /*
   * Located by label, not by index, and the row is searched for rather than
   * assumed: a wordmark band now sits above the header, and the column set may
   * change again. Counting would silently drop every picture a row out of
   * place, or onto a data column.
   */
  let headerRow = 0;
  let urlCol = null;
  let photoCol = null;
  for (let n = 1; n <= Math.min(5, ws.rowCount); n++) {
    const vals = ws.getRow(n).values;
    let u = null;
    let p = null;
    vals.forEach((v, i) => {
      if (v === "Objective Evidence") u = i;
      if (v === PHOTO_COL_LABEL) p = i;
    });
    if (u && p) { headerRow = n; urlCol = u; photoCol = p; break; }
  }
  if (!headerRow) return 0;

  let placed = 0;
  for (let n = headerRow + 1; n <= ws.rowCount; n++) {
    const cell = ws.getRow(n).getCell(urlCol).value;
    const urls = String(cell && cell.text ? cell.text : cell || "")
      .split("\n").map((u) => u.trim()).filter(Boolean);
    const shot = urls.find((u) => images.get(u));
    if (!shot) continue;

    try {
      const id = wb.addImage({ buffer: images.get(shot), extension: "jpeg" });
      ws.getRow(n).height = PHOTO_ROW_HEIGHT;
      ws.addImage(id, {
        // nativeCol is 0-based; the header index is 1-based.
        tl: { col: photoCol - 1 + 0.1, row: n - 1 + 0.1 },
        ext: PHOTO_PX,
        editAs: "oneCell",
      });
      placed++;
    } catch (e) {
      /* this row keeps its URL and loses its picture; the rest carry on */
    }
  }

  return placed;
}

function scoresSheet(wb, model, mark) {
  const d = model.sections.find((x) => x.key === "score_analysis").data;
  const ws = wb.addWorksheet("Scores");
  const top = stampLogo(ws, mark);
  ws.views = [{ state: "frozen", ySplit: top + 1 }];

  ws.columns = [
    { width: 8 }, { width: 34 }, { width: 40 }, { width: 12 }, { width: 10 }, { width: 14 },
  ];
  styleHeader(ws.addRow(["Pillar", "Pillar name", "Sub-pillar", "Assessed", "Score", "Band"]));

  d.pillars.forEach((p) => {
    const head = ws.addRow([p.no, p.name, "— pillar total —", `${p.assessed}/${p.total}`,
      p.pct === null ? "" : p.pct / 100, p.band]);
    head.font = { bold: true };
    head.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.sunk } };
    head.getCell(5).numFmt = "0.0%";

    p.sub_pillars.forEach((s) => {
      const row = ws.addRow(["", "", s.name, `${s.assessed}/${s.total}`,
        s.pct === null ? "" : s.pct / 100, s.band]);
      row.getCell(5).numFmt = "0.0%";
    });
  });

  return ws;
}

function scopeSheet(wb, model, mark) {
  const d = model.sections.find((x) => x.key === "scope").data;
  if (!d.exclusions.length) return null;

  const ws = wb.addWorksheet("Scope");
  const top = stampLogo(ws, mark);
  ws.views = [{ state: "frozen", ySplit: top + 1 }];
  ws.columns = [{ width: 34 }, { width: 12 }, { width: 80 }];
  styleHeader(ws.addRow(["Excluded", "Level", "Reason"]));

  d.exclusions.forEach((e) => {
    const row = ws.addRow([e.name, e.level === "sub_pillar" ? "Sub-pillar" : "Element", e.reason]);
    row.alignment = { vertical: "top", wrapText: true };
  });

  return ws;
}

/* ------------------------------------------------------------------ */

/** Build the workbook. Separated from saving so it can be tested. */
/**
 * The workbook, with URLs and no pictures.
 *
 * Deliberately knows nothing about photographs: it always succeeds, and
 * `insertPhotos` adds what it can afterwards.
 */
export async function buildWorkbook(model, logo) {
  const wb = new ExcelJS.Workbook();

  /*
   * Registered once and stamped on every sheet. Registering per sheet embeds
   * the file four times, which is four times the bytes for one picture.
   */
  const mark = logo ? wb.addImage({ buffer: logo, extension: "png" }) : null;
  wb.creator = model.header.prepared_by || "Momentum";
  wb.created = new Date();
  wb.title = `CSA — ${model.header.organization} — ${model.header.site}`;

  summarySheet(wb, model, mark);
  findingsSheet(wb, model, mark);
  scoresSheet(wb, model, mark);
  scopeSheet(wb, model, mark);

  return wb;
}

export async function exportReportExcel(model, opts = {}) {
  /*
   * Build, then fetch, then insert, then write.
   *
   * The workbook is finished before a single picture is touched, so the file
   * survives anything the photographs do. Fetching stays pooled — eight at a
   * time — because that is the part that waits on a network; the insertion
   * afterwards is in memory and its order costs nothing.
   */
  // The wordmark is same-origin, so it fetches where the app runs.
  const wb = await buildWorkbook(model, await assetBytes(logoArt));
  // Through the API, which works wherever the app does; S3 is the fallback.
  const images = await fetchPhotos(model, opts.photoBytes);
  insertPhotos(wb, images);
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const name = opts.fileName || fileNameFor(model, "xlsx");
  saveAs(blob, name);
  return { name, bytes: blob.size, photos: photoReport(images) };
}
